import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { PerInfoBox } from '../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { LoadingAndErrors } from '../../components/custom_components/LoadingAndErrors'
import PerText from '../../components/custom_components/Text'
import { useCollapse } from '../../routes/collapse-context'
import { useScoreGridsStore } from './ScoreGridsStore'
import { ScoreGridsPageSelector } from './ScoreGridsPageSelector'
import { ScoreGridsPageTabs } from './ScoreGridsPageTabs'
import { useAuth } from '../../context/auth-context'
import { RoleName } from '../../types'
import useManagerOfCompanyOrResponsibleOf from '../../graphql/company/hooks/useManagerOfCompanyOrResponsibleOf'

export const ScoreGridsPage: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [initStore, storeInitialized, resetStore] = useScoreGridsStore(
    (state) => [state.initStore, state.storeInitialized, state.resetStore],
    shallow,
  )

  const { data, error, loading } = useManagerOfCompanyOrResponsibleOf()
  const collapsed = useCollapse()

  useEffect(() => {
    if (
      !!data &&
      data.findCompaniesAndCountriesByCompanyManagerOrResponsible &&
      data.findCompaniesAndCountriesByCompanyManagerOrResponsible.length
    ) {
      initStore(data.findCompaniesAndCountriesByCompanyManagerOrResponsible)
    }
    return () => {
      resetStore()
    }
  }, [data, initStore, resetStore])

  if (
    loading ||
    error ||
    !data ||
    !data.findCompaniesAndCountriesByCompanyManagerOrResponsible ||
    (!!data.findCompaniesAndCountriesByCompanyManagerOrResponsible.length && !storeInitialized)
  ) {
    return (
      <ContentContainer collapsed={collapsed}>
        <LoadingAndErrors
          isLoading={loading}
          hasNoData={!data || !data.findCompaniesAndCountriesByCompanyManagerOrResponsible}
          isPermissionDenied={!error && (!user.roles || !user.roles.includes(RoleName.CompanyManager))}
          error={error}
        />
      </ContentContainer>
    )
  } else if (data.findCompaniesAndCountriesByCompanyManagerOrResponsible.length === 0) {
    return (
      <PerInfoBox>
        <InfoBoxTile
          heading={t('scoreGridsView.empty.heading')}
          headingAlign="center"
          contentAlign="center"
          headingColor="secondary"
          headingMt="xl"
        >
          <PerText>{t('scoreGridsView.empty.message')}</PerText>
        </InfoBoxTile>
      </PerInfoBox>
    )
  }

  return (
    <ContentContainer collapsed={collapsed}>
      <ScoreGridsPageSelector />
      <ScoreGridsPageTabs />
    </ContentContainer>
  )
}
